import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { TrendingUp as TrendingUp2910 } from 'lucide-react'; // FIN_DESTAQUE_2910
import { Phone, Wallet, KeyRound, Scale, DollarSign, Bell, LineChart, ScrollText, Zap, UserX, ArrowRightLeft, FolderOpen } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { API_BASE_URL } from '../apiBase';
import { applyAppearanceColors } from '../lib/brandPrimary';
import { applyBrowserBrand } from '../lib/applyBrowserBrand';
import { applyPwaBrand } from '../lib/pwaInstall';
import { NavCommandPalette } from './NavCommandPalette';
import { DesktopQuickActionsModal } from './DesktopQuickActionsModal';
import AppUpdateModal from './AppUpdateModal';
import MigrationBonusClaimModal from './MigrationBonusClaimModal';
import PwaInstallModal from './PwaInstallModal';
import OfficeLayout from './office/OfficeLayout';
import { usesModernOfficeVisual } from '../lib/officeVisual';
import { completeOfficeNavigation } from './office/officeNavigationPresentation';
import { useAuraxFlags } from '../lib/auraxFlags';
import { hidePath2810, useBodyFlags2810 } from '../lib/semApps2810'; // SEM_ATIVAR_APPS_2810
import '../lib/statsMobile2910.css'; // STATS_MOBILE_2910 (switch private/STATS_MOBILE_2910_OFF)
import { useCompacto2910, useLimpaTopo2910 } from '../lib/compacto2910'; // COMPACTO_GERAL_2910 (switch private/COMPACTO_GERAL_2910_OFF)
import { reorderMenu2810, MenuOrdemExtras2810 } from '../lib/menuOrdem2810';
import '../lib/PaginasLimpas2810.css'; // PAGINAS_LIMPAS_2810
import OfficeModalHeader from './office/OfficeModalHeader';
import { OFFICE_MODULES, OFFICE_TOOL_LINKS, visibleToolLinks, navItemVisible } from './office/navModules';
import { Sparkles as Sparkles2910 } from 'lucide-react'; // EXTRAS_MENU_2910
import AttendantClockBar from './AttendantClockBar';
import MeetingAlertsBridge from './MeetingAlertsBridge';
import LiveFloatingDock from './LiveFloatingDock';
import TourExtras2910 from './TourExtras2910'; // TOUR_EXTRAS_2910
import { useOfficeHeartbeat } from '../hooks/useOfficePresence';
import useOwnPlayerAccess from '../hooks/useOwnPlayerAccess';
import { attendanceBadgeCount } from '../lib/chatBadges';
import '../lib/seletorPainel0211'; // SELETOR_PAINEL_0211
import SeletorPainel0211 from './SeletorPainel0211';

const TOOL_ICONS = {
  '/gestor-xui/credential-generation': KeyRound,
  '/gestor-xui/reseller-billing': Scale,
  '/gestor-xui/my-reseller-billing': Scale,
  '/gestor-xui/gateway': DollarSign,
  '/gestor-xui/credit-packs': Wallet,
  '/gestor-xui/checkout-notifications': Bell,
  '/gestor-xui/sales': LineChart,
  '/gestor-xui/credit-logs': ScrollText,
  '/triggers': Zap,
  '/gestor-xui/inactive-clients': UserX,
  '/gestor-xui/inactive-resellers': UserX,
  '/gestor-xui/reseller-migration': ArrowRightLeft,
  '/admin/materiais': FolderOpen,
};

const API_URL = API_BASE_URL;
const WA_SETUP_OPEN_EVENT = 'aurax:wa-setup';

const AppShell = ({ children }) => {
  const { user, logout, isAdmin, accountSummary, isImpersonating, stopImpersonation } = useAuth();
  useOfficeHeartbeat(user, isImpersonating);
  const ownPlayerAccess = useOwnPlayerAccess().status === 'allowed';
  const { syncOfficeBranding } = useTheme();
  const { translateModules, translateTools, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [totalUnread, setTotalUnread] = useState(0);
  const [manualUnread, setManualUnread] = useState(0);
  const [settings, setSettings] = useState({
    brand_name: 'Dragon Tech',
    logo_url: '',
    logo_url_light: '',
    logo_url_dark: '',
    pwa_icon_url: '',
    community_whatsapp_url: '',
    community_telegram_url: '',
    community_youtube_url: '',
    support_whatsapp: '',
    support_label: 'Suporte',
  });
  const [waSetupOpen, setWaSetupOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [leavingImpersonation, setLeavingImpersonation] = useState(false);
  const [attendantWorkStatus, setAttendantWorkStatus] = useState(
    user?.role === 'attendant' ? user?.attendance_status || 'off' : 'available',
  );

  useEffect(() => {
    if (user?.role !== 'attendant') {
      setAttendantWorkStatus('available');
      return undefined;
    }
    const onStatus = (event) => setAttendantWorkStatus(String(event?.detail?.status || 'off'));
    window.addEventListener('aurax:attendance-status', onStatus);
    return () => window.removeEventListener('aurax:attendance-status', onStatus);
  }, [user?.role]);

  useEffect(() => {
    const openWallet = () => navigate('/buy-credits');
    window.addEventListener('aurax:open-postpaid-wallet', openWallet);
    return () => window.removeEventListener('aurax:open-postpaid-wallet', openWallet);
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;
    const applyUnread = (n) => {
      setTotalUnread(Math.max(0, Number(n) || 0));
    };
    const onChatBadge = (e) => {
      applyUnread(attendanceBadgeCount(e?.detail));
    };
    window.addEventListener('aurax:chat-badge', onChatBadge);
    // Em /chats o Chats.js já faz fetchCounts e emite aurax:chat-badge — evita pedido duplicado.
    if ((location.pathname || '').startsWith('/chats')) {
      return () => window.removeEventListener('aurax:chat-badge', onChatBadge);
    }
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/conversations/counts/tabs`, { withCredentials: true });
        // O menu lateral representa conversas que ainda aguardam resposta.
        // Apenas abrir/ler uma conversa não pode escondê-las.
        applyUnread(attendanceBadgeCount(data));
      } catch { /* keep last */ }
    };
    tick();
    const i = setInterval(tick, 45000);
    return () => {
      clearInterval(i);
      window.removeEventListener('aurax:chat-badge', onChatBadge);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || isAdmin) return;
    const modules = Array.isArray(accountSummary?.modules) ? accountSummary.modules : [];
    const canManual =
      modules.includes('*') || modules.includes('reseller_manual');
    if (!canManual && modules.length > 0) {
      setManualUnread(0);
      return undefined;
    }
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/reseller-manual/badge`, {
          withCredentials: true,
        });
        setManualUnread(typeof data?.unread_count === 'number' ? data.unread_count : 0);
      } catch {
        /* keep last */
      }
    };
    tick();
    const i = setInterval(tick, 60000);
    return () => clearInterval(i);
  }, [user, isAdmin, accountSummary?.modules]);

  const appearanceSigRef = useRef('');

  useEffect(() => {
    if (!user) return;
    const loadAppearance = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/account/appearance`, { withCredentials: true });
        if (!data) return;
        const sig = [
          data.brand_name,
          data.logo_url,
          data.logo_url_light,
          data.logo_url_dark,
          data.pwa_icon_url,
          data.brand_theme,
          data.brand_accent,
          data.primary_color,
          data.browser_title,
          data.favicon_url,
          data.support_whatsapp, data.support_label, JSON.stringify(data.support_contacts),
          data.community_whatsapp_url, data.community_telegram_url, data.community_youtube_url,
        ].join('|');
        if (sig === appearanceSigRef.current) return;
        appearanceSigRef.current = sig;
        setSettings({
          brand_name: data.brand_name || 'Dragon Tech',
          logo_url: data.logo_url || '',
          logo_url_light: data.logo_url_light || data.logo_url || '',
          logo_url_dark: data.logo_url_dark || data.logo_url || '',
          pwa_icon_url: data.pwa_icon_url || '',
          community_whatsapp_url: data.community_whatsapp_url || '',
          community_telegram_url: data.community_telegram_url || '',
          community_youtube_url: data.community_youtube_url || '',
          support_whatsapp: data.support_whatsapp || '',
          support_label: data.support_label || 'Suporte',
          ...(Array.isArray(data.support_contacts) ? { support_contacts: data.support_contacts } : {}),
        });
        applyBrowserBrand({
          browser_title: data.browser_title || '',
          brand_name: data.brand_name || 'Dragon Tech',
          favicon_url: data.favicon_url || '',
        });
        applyPwaBrand({
          brand_name: data.brand_name || 'Dragon Tech',
          theme_color: data.primary_color || data.brand_accent || '#0D1117',
          pwa_icon_url: data.pwa_icon_url || '',
          icon_url: data.logo_url_dark || data.logo_url || data.logo_url_light || '',
        });
        // Não depende de `resolved`: evita loop (toggle → re-fetch → brand_theme força dark)
        syncOfficeBranding({
          brand_theme: data.brand_theme || undefined,
          brand_accent: data.brand_accent ?? '',
          forceTheme: true,
          forceAccent: true,
        });
        // Cores: usar o tema já activo no DOM (preferência da conta no servidor)
        const activeTheme =
          document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
        applyAppearanceColors(
          { brand_accent: data.brand_accent || '', primary_color: data.primary_color },
          activeTheme,
          { force: true },
        );
      } catch { /* tokens padrão */ }
    };
    loadAppearance();
    const onUpdated = () => {
      appearanceSigRef.current = '';
      loadAppearance();
    };
    window.addEventListener('aurax:appearance-updated', onUpdated);
    return () => window.removeEventListener('aurax:appearance-updated', onUpdated);
  }, [user, syncOfficeBranding]);

  useEffect(() => {
    const onOpen = () => setWaSetupOpen(true);
    window.addEventListener(WA_SETUP_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(WA_SETUP_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    const down = (e) => {
      if (!(e.metaKey || e.ctrlKey) || String(e.key).toLowerCase() !== 'k') return;
      e.preventDefault();
      setCommandOpen(true);
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, []);

  useEffect(() => {
    const down = (e) => {
      if (!(e.metaKey || e.ctrlKey) || String(e.key) !== '/') return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      e.preventDefault();
      setQuickActionsOpen(true);
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleStopImpersonation = async () => {
    if (leavingImpersonation) return;
    setLeavingImpersonation(true);
    try {
      await stopImpersonation();
      navigate('/gestor-xui/resellers', { replace: true });
      window.location.reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // eslint-disable-next-line no-alert
      alert(typeof detail === 'string' ? detail : 'Não foi possível voltar à sua conta.');
    } finally {
      setLeavingImpersonation(false);
    }
  };

  const modulesAvail = useMemo(() => {
    const raw = accountSummary?.modules;
    if (Array.isArray(raw) && raw.length > 0) return raw;
    if (isAdmin) return ['*'];
    return Array.isArray(raw) ? raw : [];
  }, [accountSummary?.modules, isAdmin]);

  const can = (key) => !key || modulesAvail.includes('*') || modulesAvail.includes(key);

  const hide2810Flags = useAuraxFlags(); // SEM_ATIVAR_APPS_2810 / SEM_CARTEIRA_2810
  useBodyFlags2810(hide2810Flags);
  useCompacto2910(!!hide2810Flags?.compacto_geral_2910); // COMPACTO_GERAL_2910
  useLimpaTopo2910(!!hide2810Flags?.limpa_topo_2910); // LIMPA_TOPO_2910
  useEffect(() => { const on2910 = hide2810Flags?.stats_mobile_2910 !== false; if (on2910) document.body.setAttribute('data-stats-mobile-2910', '1'); else document.body.removeAttribute('data-stats-mobile-2910'); }, [hide2810Flags?.stats_mobile_2910]); // STATS_MOBILE_2910
  useEffect(() => { const on3010 = user?.role && user.role !== "admin" && hide2810Flags?.fin_revenda_3010 !== false; if (on3010) document.body.setAttribute("data-fin-base3010", "1"); else document.body.removeAttribute("data-fin-base3010"); }, [user?.role, hide2810Flags?.fin_revenda_3010]); // FIN_REVENDA_3010
  const filteredModules = useMemo(() => {
    const billing =
      accountSummary?.user?.billing_mode || user?.billing_mode || 'prepaid';
    const mapped = OFFICE_MODULES.map((mod) => {
      if (mod.adminOnly && !isAdmin) return null;
      const visibleItems = mod.items.filter((it) => !hidePath2810(it.path, hide2810Flags) &&
        navItemVisible(it, { isAdmin, isAttendant: user?.role === 'attendant', can, billing, ownPlayerAccess }),
      );
      if (visibleItems.length === 0) return null;
      const first = visibleItems[0].path;
      return {
        ...mod,
        items: visibleItems,
        defaultPath: visibleItems.some((i) => i.path === mod.defaultPath) ? mod.defaultPath : first,
      };
    }).filter(Boolean);
    return translateModules(mapped);
  }, [isAdmin, accountSummary, modulesAvail, user?.billing_mode, user?.role, translateModules, ownPlayerAccess, hide2810Flags]);

  const paletteModules = useMemo(() => {
    const billing =
      accountSummary?.user?.billing_mode || user?.billing_mode || 'prepaid';
    const tools = visibleToolLinks({
      isAdmin,
      isAttendant: user?.role === 'attendant',
      can,
      billing,
      hubPaths: accountSummary?.office_tools_paths,
      aiStudioAccess: user?.ai_studio_access === true,
    }).map((tool) => ({
      path: tool.path,
      label: tool.label,
      icon: TOOL_ICONS[tool.path] || Zap,
      permKey: tool.permKey,
    }));
    const translatedTools = translateTools(tools);
    const ferramentasModule = {
      id: 'ferramentas',
      label: t('nav.tools'),
      items: translatedTools.map((tool) => ({
        path: tool.path,
        label: tool.label,
        icon: TOOL_ICONS[tool.path] || Zap,
        permKey: tool.permKey,
      })),
      defaultPath: translatedTools[0]?.path,
    };
    if (!translatedTools.length) return filteredModules;
    return [...filteredModules, { ...ferramentasModule, items: (ferramentasModule.items || []).filter((i) => !hidePath2810(i.path, hide2810Flags)) }];
  }, [filteredModules, isAdmin, modulesAvail, accountSummary, user?.billing_mode, user?.role, user?.ai_studio_access, translateTools, t, hide2810Flags]);

  const auraxFlags2810 = useAuraxFlags();
  // EXTRAS_FIX_2910: rota direta de módulo não adicionado -> manda para Extras
  useEffect(() => {
    const f = auraxFlags2810;
    if (!f?.extras_2910 || !Array.isArray(f.extras_hide_2910) || !f.extras_hide_2910.length) return;
    const p = String(location.pathname || '').replace(/\/+$/, '');
    if (f.extras_hide_2910.some((h) => p === h || p.startsWith(`${h}/`))) navigate('/extras', { replace: true });
  }, [auraxFlags2810, location.pathname, navigate]);
  const menuOrdem2810 = !!auraxFlags2810?.menu_ordem;
  const menuEnxuto2810 = menuOrdem2810 && !!auraxFlags2810?.menu_enxuto; // MENU_ENXUTO_2810
  const menuWhats2810 = menuOrdem2810 && !!auraxFlags2810?.menu_whats; // MENU_WHATS_2810
  const revendaFix2810 = menuOrdem2810 && !!auraxFlags2810?.auditoria_revenda; // AUDITORIA_REVENDA_2810
  // AGRUPA_2810: grupos ligados (switch private/AGRUPA_<GRUPO>_2810_OFF)
  const agrupaKey2810 = menuOrdem2810 ? ['financeiro', 'clientes', 'catalogo', 'ajustes', 'revendas', 'comunicacao', 'materiais', 'registros'].filter((k) => auraxFlags2810?.[`agrupa_${k}`]).concat(auraxFlags2810?.creditos_header ? ['creditos_header'] : []).concat(auraxFlags2810?.fin_revenda_2910 ? ['fin_revenda'] : []).concat(auraxFlags2810 && auraxFlags2810.conteudos_3010 !== false ? ['conteudos_3010'] : []).join(',') : ''; // CREDITOS_HEADER_2810
  const agrupa2810 = useMemo(() => (agrupaKey2810 ? Object.fromEntries(agrupaKey2810.split(',').map((k) => [k, true])) : null), [agrupaKey2810]);
  const navPaths2810 = useMemo(() => {
    if (!agrupa2810) return undefined;
    const base = completeOfficeNavigation(filteredModules, paletteModules);
    return new Set(base.flatMap((g) => (g.items || []).map((i) => String(i.path || '').split('?')[0])));
  }, [agrupa2810, filteredModules, paletteModules]);
  const officeNavigationModules = useMemo(() => {
    const base = usesModernOfficeVisual(user)
      ? completeOfficeNavigation(filteredModules, paletteModules)
      : filteredModules;
    // MENU_ORDEM_2810 (GROK2): nova ordem/agrupamento; switch private/MENU_ORDEM_2810_OFF
    const mods2810 = menuOrdem2810 ? reorderMenu2810(base, { enxuto: menuEnxuto2810, revendaFix: revendaFix2810, whats: menuWhats2810, chatsWhats: menuWhats2810 && !!auraxFlags2810?.chats_whats, isAdmin: user?.role === 'admin', agrupa: agrupa2810 }) : base;
    const fin2910 = auraxFlags2810?.fin_destaque_2910 ? finDestaqueMenu2910(mods2810, { forcar3010: user?.role !== "admin" && auraxFlags2810?.fin_revenda_3010 !== false }) : mods2810; // FIN_DESTAQUE_2910 + FIN_REVENDA_3010
    const cont3010 = auraxFlags2810 && auraxFlags2810.conteudos_3010 !== false ? conteudosMenu3010(fin2910, { rev: user?.role !== 'admin' && auraxFlags2810?.sem_selo_rec_3010 === false /* SEM_SELO_REC_3010: selo Recomendado só volta com private/SEM_SELO_REC_3010_OFF */ }) : fin2910; // CONTEUDOS_3010
    const canEq0311 = (k) => (k === 'team' && auraxFlags2810?.equipe_0211 && user?.role === 'reseller') || can(k); // EQUIPE_V2_0311: Equipe liberada p/ toda revenda
    const out3010a = auraxFlags2810?.extras_menu_2910 && user?.role !== 'admin' ? extrasMenu2910(cont3010, auraxFlags2810, canEq0311) : cont3010; // EXTRAS_MENU_2910
    const out3010 = user?.role === 'reseller' && auraxFlags2810?.equipe_0211 ? out3010a.map((g) => ({ ...g, items: (g.items || []).map((it) => (it.path === '/team' ? { ...it, label: 'Equipes', rec3010: true } : it)) })) : out3010a; // EQUIPE_V2_0311
    const fin3010 = auraxFlags2810?.fin_destaque_2910 && user?.role !== 'admin' && auraxFlags2810?.fin_revenda_3010 !== false;
    // MENU_SO_WHATSAPP_0311 (GROK2): some Atendimentos/DataCrazy Chat/Funil; tudo dentro de WhatsApp. Switch private/MENU_SO_WHATSAPP_OFF
    const soW0311 = (arr) => { if (!auraxFlags2810?.menu_so_whatsapp) return arr; const fora = new Set(['/chats', '/atendimento', '/funnel']); return arr.map((g) => { const its = (g.items || []).filter((i) => !fora.has(i.path)); return { ...g, items: its, defaultPath: fora.has(g.defaultPath) ? its[0]?.path : g.defaultPath }; }).filter((g) => (g.items || []).length); };
    return soW0311(fin3010 ? uniqIds3010(dedupeFin3010(out3010)) : out3010); // FIN_REVENDA_3010 (+ ids de grupo únicos: id repetido deixava item fantasma no DOM)
  }, [modulesAvail, auraxFlags2810?.extras_menu_2910, auraxFlags2810?.extras_added_2910, user, filteredModules, paletteModules, menuOrdem2810, menuEnxuto2810, revendaFix2810, menuWhats2810, agrupa2810, auraxFlags2810?.chats_whats, auraxFlags2810?.fin_destaque_2910, auraxFlags2810?.fin_revenda_3010, auraxFlags2810?.conteudos_3010, auraxFlags2810?.sem_selo_rec_3010, auraxFlags2810?.menu_so_whatsapp]); // MENU_SO_WHATSAPP_0311 fix2: dep
  const allowedPaths2810 = useMemo(() => {
    if (!revendaFix2810) return undefined; // AUDITORIA_REVENDA_2810
    const base = completeOfficeNavigation(filteredModules, paletteModules);
    return new Set([...base, ...officeNavigationModules].flatMap((g) => (g.items || []).map((i) => String(i.path || '').split('?')[0]))); // EXTRAS_MENU_2910
  }, [revendaFix2810, filteredModules, paletteModules, officeNavigationModules]);

  const badges = { totalUnread, manualUnread };

  const handleOpenQuickTestFromDesktop = () => {
    try { sessionStorage.setItem('aurax_open_quick_test', '1'); } catch { /* ignore */ }
    navigate('/dashboard');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('aurax:open-quick-test'));
    }, 180);
  };

  return (
    <>
      <OfficeLayout
        pathname={location.pathname}
        modules={officeNavigationModules}
        badges={badges}
        settings={settings}
        user={user}
        accountSummary={accountSummary}
        onLogout={handleLogout}
        onOpenCommandPalette={() => setCommandOpen(true)}
      >
        {isImpersonating ? (
          <div
            className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-sm text-amber-950 dark:text-amber-100"
            data-testid="impersonation-banner"
          >
            <span>
              Você está na conta de{' '}
              <strong>{user?.name || user?.email || 'revendedor'}</strong>
              {user?.impersonator_email ? (
                <>
                  {' '}
                  (sessão de <strong>{user.impersonator_email}</strong>)
                </>
              ) : null}
              .
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 border-amber-600/40 bg-background/80"
              disabled={leavingImpersonation}
              onClick={handleStopImpersonation}
            >
              {leavingImpersonation ? 'A voltar…' : 'Voltar à minha conta'}
            </Button>
          </div>
        ) : null}
        {accountSummary?.recharge_only ? (
          <div
            className="mb-3 rounded-xl border border-orange-500/35 bg-orange-500/10 px-3 py-2 text-sm text-orange-950 dark:text-orange-100"
            data-testid="recharge-only-banner"
          >
            {accountSummary.recharge_only_message ||
              'Sua conta está inativa. Você pode entrar apenas para comprar créditos. Após a recarga, o acesso completo é restaurado.'}
          </div>
        ) : null}
        <AttendantClockBar className="mb-3" compact={(location.pathname || '').startsWith('/chats')} />
        {user?.role === 'attendant' && attendantWorkStatus !== 'available' ? (
          <section className="mx-auto flex min-h-[46vh] w-full max-w-2xl flex-col items-center justify-center rounded-2xl border border-amber-500/30 bg-amber-500/10 p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground">Você está em pausa</h2>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-muted-foreground">
              Aproveite este período. Quando estiver pronto para retomar a fila, altere seu status para
              <strong className="text-foreground"> Atendendo</strong>. As ações operacionais ficam pausadas enquanto
              você estiver ausente, evitando atendimentos atribuídos por engano.
            </p>
          </section>
        ) : (<>{menuOrdem2810 ? <MenuOrdemExtras2810 pathname={location.pathname} isAdmin={user?.role === 'admin'} enxuto={menuEnxuto2810} permRevenda={!!auraxFlags2810?.permissoes_revenda} /* PERMISSOES_REVENDA_2810 */ allowedPaths={allowedPaths2810} agrupa={agrupa2810} navPaths={navPaths2810} /* AGRUPA_2810 */ /> : null}<SeletorPainel0211 pathname={location.pathname} />{children ?? <Outlet />}</>)}
      </OfficeLayout>
      <MeetingAlertsBridge />
      <LiveFloatingDock />
      <TourExtras2910 />

      <Dialog open={waSetupOpen} onOpenChange={setWaSetupOpen}>
        <DialogContent className="max-w-md">
          <OfficeModalHeader
            layout="row"
            variant="circle"
            icon={Phone}
            title="Automação pelo WhatsApp"
            description="A conexão é opcional. Configure somente se quiser enviar dados, notificações e lembretes automáticos pela sua revenda."
          />
          <div className="flex flex-col gap-2 pt-2">
            <Button
              className="w-full"
              onClick={() => {
                setWaSetupOpen(false);
                navigate('/connections');
              }}
            >
              Ir para WhatsApp
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => setWaSetupOpen(false)}
            >
              Continuar sem WhatsApp
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <NavCommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        modules={paletteModules}
        badges={badges}
      />
      <DesktopQuickActionsModal
        open={quickActionsOpen}
        onOpenChange={setQuickActionsOpen}
        modules={filteredModules}
        onOpenQuickTest={handleOpenQuickTestFromDesktop}
        onNavigate={navigate}
      />
      <AppUpdateModal userId={user?.id || user?._id || "anonymous"} />
      <MigrationBonusClaimModal />
      <PwaInstallModal
        brandName={settings.brand_name || 'Dragon Tech'}
        logoUrl={settings.logo_url_dark || settings.logo_url || settings.logo_url_light || ''}
        pwaIconUrl={
          settings.pwa_icon_url
          || settings.logo_url_dark
          || settings.logo_url
          || settings.logo_url_light
          || ''
        }
        openGate={!waSetupOpen}
      />
    </>
  );
};


// FIN_DESTAQUE_2910 (GROK2): Financeiro vira o 2o item (logo apos Dashboard), com destaque. Switch private/FIN_DESTAQUE_2910_OFF.
function finDestaqueMenu2910(groups = [], opts3010 = {}) {
  let fin = null;
  const isFin = (it) => it && (it.agrupa2810 === 'financeiro' || String(it.path || '').split('?')[0] === '/financeiro');
  const rest = groups.map((g) => {
    const items = (g.items || []).filter((it) => { if (opts3010.forcar3010 && isFin(it) && !it.destaque2910) { fin = fin || it; return false; } if (!fin && isFin(it)) { fin = it; return false; } return true; }); // FIN_REVENDA_3010: revenda nunca fica com Financeiro duplicado
    return items.length ? { ...g, items, defaultPath: items[0].path } : null;
  }).filter(Boolean);
  if (!fin && opts3010.forcar3010) fin = { path: '/financeiro', label: 'Financeiro' }; // FIN_REVENDA_3010: revenda sem módulo financeiro ganha a aba (dados só dela)
  if (!fin) return groups;
  const item = { ...fin, path: '/financeiro', label: 'Financeiro', icon: TrendingUp2910, destaque2910: true };
  const gi = rest.findIndex((g) => (g.items || []).some((it) => it.path === '/dashboard'));
  if (gi < 0) { rest.unshift({ ...(groups[0] || {}), items: [item], defaultPath: item.path }); return rest; }
  const items = [...rest[gi].items];
  items.splice(items.findIndex((it) => it.path === '/dashboard') + 1, 0, item);
  rest[gi] = { ...rest[gi], items };
  return rest;
}

// CONTEUDOS_3010 (switch private/CONTEUDOS_3010_OFF): Conteúdos (busca de filmes/séries/canais) vira item próprio logo após Financeiro.
// Só move o item que o usuário já tem (permissão mantida). Revenda: Financeiro e Conteúdos marcados como Recomendado.
function conteudosMenu3010(groups = [], { rev } = {}) {
  const isC = (it) => String(it.path || '').split('?')[0] === '/gestor-xui/catalog';
  let cont = null;
  let rest = groups.map((g) => { const items = (g.items || []).filter((it) => { if (isC(it)) { cont = cont || it; return false; } return true; }); return items.length ? { ...g, items, defaultPath: items[0].path } : null; }).filter(Boolean);
  if (rev) rest = rest.map((g) => ({ ...g, items: (g.items || []).map((it) => (it.destaque2910 ? { ...it, rec3010: true } : it)) }));
  if (!cont) return rest;
  const item = { ...cont, label: 'Conteúdos', rec3010: !!rev, cont3010: true };
  const gi = rest.findIndex((g) => (g.items || []).some((it) => it.destaque2910 || it.path === '/dashboard'));
  if (gi < 0) { rest.unshift({ ...(groups[0] || {}), items: [item], defaultPath: item.path }); return rest; }
  const items = [...rest[gi].items];
  let at = items.findIndex((it) => it.destaque2910); if (at < 0) at = items.findIndex((it) => it.path === '/dashboard');
  items.splice(at + 1, 0, item);
  rest[gi] = { ...rest[gi], items };
  return rest;
}

export default AppShell;

// EXTRAS_MENU_2910 (switch private/EXTRAS_MENU_2910_OFF): extra adicionado sempre aparece no menu (grupo "Meus extras"),
// mesmo quando o menu reorganizado não tinha lugar para ele. Respeita a permissão do módulo (can).
export const EXTRAS_PATHS_2910 = { equipe: '/team', reunioes: '/reunioes', recuperacao: '/recuperacao', gatilhos: '/triggers', chatbot: '/triggers/chatbot', atendimentos: '/chats', whatsapp: '/connections', modelos: '/notification-templates', disparo: '/recuperacao', materiais: '/materiais' };
// FIN_REVENDA_3010: mantém só um Financeiro (o destacado, 2º item) no menu da revenda
// FIN_REVENDA_3010: React usa mod.id como key; ids repetidos (ex.: b-inicio 2x) deixavam nó antigo duplicado
function uniqIds3010(groups = []) { const seen = {}; return groups.map((g) => { const id = g.id || 'g'; seen[id] = (seen[id] || 0) + 1; return seen[id] > 1 ? { ...g, id: id + '-' + seen[id] } : g; }); }
function dedupeFin3010(groups = []) {
  let visto = false;
  const isF = (it) => String(it.path || '').split('?')[0] === '/financeiro';
  const gd = groups.findIndex((g) => (g.items || []).some((it) => it.path === '/dashboard') && (g.items || []).some(isF));
  if (gd >= 0) return groups.map((g, i) => { const items = (g.items || []).filter((it) => !isF(it) || (i === gd && !visto && (visto = true))); return items.length ? { ...g, items } : null; }).filter(Boolean);
  return groups.map((g) => { const items = (g.items || []).filter((it) => { if (String(it.path || '').split('?')[0] !== '/financeiro') return true; if (it.destaque2910 && !visto) { visto = true; return true; } return false; }); return items.length ? { ...g, items } : null; }).filter(Boolean);
}
function extrasMenu2910(groups = [], flags = {}, can = () => true) {
  const added = Array.isArray(flags.extras_added_2910) ? flags.extras_added_2910 : [];
  if (!added.length) return groups;
  const has = new Set(groups.flatMap((g) => (g.items || []).map((i) => String(i.path || '').split('?')[0])));
  const defs = [...OFFICE_MODULES.flatMap((m) => m.items || []), ...OFFICE_TOOL_LINKS];
  const novos = [];
  added.forEach((k) => {
    const p = EXTRAS_PATHS_2910[k];
    if (!p || has.has(p)) return;
    const d = defs.find((x) => x.path === p);
    if (!d || (d.permKey && !can(d.permKey))) return;
    has.add(p); novos.push({ ...d, icon: d.icon || Sparkles2910, extra2910: k });
  });
  if (!novos.length) return groups;
  const gi = groups.findIndex((g) => (g.items || []).some((i) => i.path === '/extras'));
  const out = [...groups];
  out.splice(gi < 0 ? out.length : gi + 1, 0, { id: 'extras2910', label: 'Meus extras', icon: Sparkles2910, items: novos, defaultPath: novos[0].path });
  return out;
}
